import { Comment, Fragment, Text, type VNode, type VNodeArrayChildren, type VNodeChild } from "vue";
import { TEXT_NODE_TYPE } from "@/src/shared/constants/slots";

type SlotFunction = (...args: never[]) => VNode[];

type SlotCollection<TSlots> = {
  readonly [TName in keyof TSlots]: SlotFunction | undefined;
};

/**
 * Checks if a slot has meaningful content (non-empty text or non-comment nodes)
 * @param slots - Vue slots object
 * @param slotName - Name of the slot to check (defaults to 'default')
 * @param slotProps - Optional props passed to a scoped slot
 * @returns Boolean indicating if the slot has content
 */
export function hasSlotContent<TSlots extends SlotCollection<TSlots>>(slots: TSlots): boolean;

export function hasSlotContent<TSlots extends SlotCollection<TSlots>, TName extends keyof TSlots & string>(
  slots: TSlots,
  slotName: TName,
  ...slotProps: Parameters<NonNullable<TSlots[TName]>>
): boolean;

export function hasSlotContent<TSlots extends SlotCollection<TSlots>, TName extends keyof TSlots & string>(
  slots: TSlots,
  slotName?: TName,
  ...slotProps: Parameters<NonNullable<TSlots[TName]>>
): boolean {
  const slot = slots[(slotName ?? "default") as TName];

  if (!slot) return false;

  return hasContentInNodes(slot(...slotProps));
}

const hasContentInNodes = (nodes: VNodeArrayChildren): boolean => {
  return nodes.some(hasNodeContent);
};

const hasNodeContent = (node: VNodeChild): boolean => {
  if (Array.isArray(node)) return hasContentInNodes(node);
  if (!node || typeof node !== "object") return hasPrimitiveContent(node);

  if (node.type === Fragment) return hasFragmentContent(node.children);

  return hasVNodeContent(node);
};

const hasPrimitiveContent = (node: VNodeChild): boolean => {
  return typeof node === "string" ? Boolean(node.trim()) : Boolean(node);
};

const hasFragmentContent = (children: VNode["children"]): boolean => {
  if (Array.isArray(children)) return hasContentInNodes(children);

  return typeof children === "string" ? Boolean(children.trim()) : Boolean(children);
};

const hasVNodeContent = (node: VNode): boolean => {
  return isTextNodeWithContent(node) || isNamedSlotOutlet(node) || isNonEmptyNode(node);
};

const isNamedSlotOutlet = (node: VNode): boolean => {
  if (node.type !== "slot") return false;

  return typeof node.props?.name === "string" && node.props.name.length > 0;
};

const isTextNodeWithContent = (node: VNode): boolean => {
  if (node.type !== Text && node.type !== TEXT_NODE_TYPE) return false;
  return !!node.children?.toString().trim();
};

const isNonEmptyNode = (node: VNode): boolean => {
  return node.type !== Text && node.type !== TEXT_NODE_TYPE && node.type !== Comment && node.type !== "slot";
};
